/**
 * DownloadPage Component
 * Página pública de descarga + landing principal — cierra audit gap C.2
 *
 * Accesible SIN autenticación. Es la landing default (`/`) para que cualquier
 * visitante vea info del producto, descargue el agente, y (si es admin) pueda
 * ir al panel de administración con un clic.
 *
 * Detecta si el usuario ya tiene sesión activa (token persistido en authStore):
 *  - No logueado → botón "Iniciar sesión" → /auth
 *  - Logueado    → botón "Ir al panel"    → /dashboard
 */

import {
  Box,
  Container,
  Typography,
  Stack,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  Grid,
  Alert,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Computer as WindowsIcon,
  RocketLaunch as RocketIcon,
  Shield as ShieldIcon,
  Speed as SpeedIcon,
  Login as LoginIcon,
  Dashboard as DashboardIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import useAuthStore from '@/store/authStore';

// El repo es privado, así que las descargas NO pueden venir directo de GitHub
// (404 anónimo). El backend hace de proxy: baja del release rodante "latest" con
// un token server-side y lo sirve público desde el mismo origen. El CI mantiene
// ese release al día en cada push a main.
const MANIFEST_URL = '/api/v1/downloads/manifest';
const ASSET_URL = (name: string) => `/api/v1/downloads/${encodeURIComponent(name)}`;

// Fallbacks por si el manifest no carga (mismo proxy, nombres fijos del portable).
const FALLBACK_PORTABLE = {
  amd64: ASSET_URL('RemoteAssist_Portable_windows_amd64.zip'),
  arm64: ASSET_URL('RemoteAssist_Portable_windows_arm64.zip'),
};

interface ManifestAsset {
  name: string;
  url: string;
  size?: number;
  kind: string; // 'portable' | 'installer-windows' | 'installer-macos' | 'installer-linux' | 'other'
  arch?: string; // 'amd64' | 'arm64' | 'universal'
}

interface Manifest {
  tag: string;
  commit?: string;
  buildDate?: string;
  assets: ManifestAsset[];
}

interface DownloadOption {
  os: 'Windows' | 'macOS' | 'Linux';
  title: string;
  url: string;
  description: string;
  icon: typeof WindowsIcon;
  recommended?: boolean;
  installNote: string;
  architecture?: 'amd64' | 'arm64' | 'universal';
}

function portableTitle(arch?: string): string {
  if (arch === 'arm64') return 'Windows Portable (ARM64)';
  return 'Windows Portable (64-bit)';
}

// Construye las tarjetas del portable desde el manifest (con fallback estático).
function buildPortableOptions(manifest: Manifest | null): DownloadOption[] {
  const fromManifest = (manifest?.assets || []).filter((a) => a.kind === 'portable');
  const mk = (arch: 'amd64' | 'arm64', url: string): DownloadOption => ({
    os: 'Windows',
    title: portableTitle(arch),
    url,
    description:
      arch === 'arm64'
        ? 'Portable con GUI (bandeja + configuración) para Windows ARM64 (Surface Pro X, Snapdragon).'
        : 'Portable con GUI completa (ícono en bandeja + ventana de configuración). No requiere instalación. Se puede instalar como servicio desde la propia app.',
    icon: WindowsIcon,
    recommended: arch === 'amd64',
    architecture: arch,
    installNote:
      'Extraer ZIP → ejecutar remote-assist-portable.exe → listo. Para soporte desatendido, instalar como servicio desde la app.',
  });
  const amd = fromManifest.find((a) => a.arch === 'amd64');
  const arm = fromManifest.find((a) => a.arch === 'arm64');
  return [
    mk('amd64', amd?.url || FALLBACK_PORTABLE.amd64),
    mk('arm64', arm?.url || FALLBACK_PORTABLE.arm64),
  ];
}

const INSTALLER_LABELS: Record<string, string> = {
  'installer-windows': 'Instalador Windows (MSI)',
  'installer-macos': 'Instalador macOS (PKG)',
  'installer-linux': 'Paquete Linux (tar.gz)',
};

// Stage de madurez por `kind` del manifest. Refleja el estado real del producto:
//   - macOS y Linux son ALPHA: builds experimentales, pueden fallar.
//   - Windows (instalador y portable) es BETA: estable en lo cotidiano pero
//     todavía con cambios activos, no garantizado para producción crítica.
// Si en el futuro el backend expone el stage por asset, este helper se reemplaza
// por una lectura directa del manifest sin tocar la UI.
const STAGE_BY_KIND: Record<string, 'alpha' | 'beta'> = {
  portable: 'beta',
  'installer-windows': 'beta',
  'installer-macos': 'alpha',
  'installer-linux': 'alpha',
};

function stageFor(kind: string): 'alpha' | 'beta' | null {
  return STAGE_BY_KIND[kind] ?? null;
}

interface StageChipProps {
  stage: 'alpha' | 'beta';
}

// Chip visual del stage: ALPHA (info/neutral) o BETA (warning). Mismo icono
// (Science) para que sea claro que ambos son "no production-ready".
const StageChip: React.FC<StageChipProps> = ({ stage }) => {
  const isAlpha = stage === 'alpha';
  return (
    <Chip
      label={isAlpha ? 'ALPHA' : 'BETA'}
      size="small"
      icon={<ScienceIcon />}
      color={isAlpha ? 'info' : 'warning'}
      variant={isAlpha ? 'outlined' : 'filled'}
      aria-label={`Build en stage ${stage.toUpperCase()}`}
      sx={{ fontWeight: 700, letterSpacing: 0.5 }}
    />
  );
};

const FEATURES = [
  {
    icon: RocketIcon,
    title: 'Acceso remoto instantáneo',
    description: 'Conectate a cualquier máquina en segundos, sin configuración de firewall.',
  },
  {
    icon: ShieldIcon,
    title: 'Seguro por diseño',
    description:
      'Conexiones cifradas, autenticación por credenciales y control colaborativo entre administradores.',
  },
  {
    icon: SpeedIcon,
    title: 'Bajo overhead',
    description:
      'Agente liviano, conexión estable y reconexión automática cuando se recupera la red.',
  },
];

export default function DownloadPage() {
  // Detect if user is already authenticated (token persisted by Zustand).
  // If yes, show "Ir al panel" linking to the admin dashboard.
  // If no, show "Iniciar sesión" linking to the auth page.
  const authToken = useAuthStore((s) => s.token);
  const authUser = useAuthStore((s) => s.user);
  const isAuthenticated = Boolean(authToken);
  const adminHref = isAuthenticated ? '/dashboard' : '/auth';
  const adminLabel = isAuthenticated ? 'Ir al panel' : 'Iniciar sesión';
  const AdminIcon = isAuthenticated ? DashboardIcon : LoginIcon;

  const [manifest, setManifest] = useState<Manifest | null>(null);
  useEffect(() => {
    let cancelled = false;
    // no-store: el release rodante rota el contenido bajo el mismo tag, así que el
    // manifest debe leerse siempre fresco (nunca de caché del browser/proxy/SW).
    fetch(MANIFEST_URL, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: Manifest) => {
        if (!cancelled) setManifest(data);
      })
      .catch(() => {
        /* sin manifest → se usan los fallbacks del portable */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const downloadOptions = buildPortableOptions(manifest);
  const installers = (manifest?.assets || []).filter((a) => a.kind.startsWith('installer-'));
  const buildLabel = manifest?.commit ? `build ${manifest.commit.slice(0, 8)}` : 'última versión';
  const buildDate = manifest?.buildDate ? new Date(manifest.buildDate).toLocaleDateString() : '';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero */}
      <Box
        sx={{
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #0a1929 0%, #1a2332 50%, #0d47a1 100%)'
              : 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 50%, #64b5f6 100%)',
          py: { xs: 6, md: 10 },
          px: 2,
        }}
      >
        <Container maxWidth="md">
          <Stack spacing={3} alignItems="center" textAlign="center">
            <Stack direction="row" spacing={1}>
              <Chip
                label={buildDate ? `${buildLabel} · ${buildDate}` : buildLabel}
                color="primary"
                size="small"
              />
              {isAuthenticated && (
                <Chip
                  label={`Sesión activa: ${authUser?.username ?? 'admin'}`}
                  color="success"
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
            <Typography
              variant="h2"
              component="h1"
              fontWeight={700}
              sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}
            >
              Remote Assist
            </Typography>
            <Typography variant="h5" component="p" color="text.secondary" fontWeight={400}>
              Plataforma de asistencia remota. Soporte técnico profesional para tu equipo.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 2 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<DownloadIcon />}
                href="#downloads"
                sx={{ minWidth: 200 }}
              >
                Descargar agente
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<AdminIcon />}
                href={adminHref}
                sx={{ minWidth: 200 }}
              >
                {adminLabel}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Features */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
        <Typography variant="h4" component="h2" textAlign="center" fontWeight={600} gutterBottom>
          ¿Por qué Remote Assist?
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 5 }}>
          Construido para equipos técnicos que necesitan control total sobre su infraestructura de
          soporte.
        </Typography>
        <Grid container spacing={4}>
          {FEATURES.map((feature) => {
            const Icon = feature.icon;
            return (
              <Grid size={{ xs: 12, md: 4 }} key={feature.title}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                      }}
                    >
                      <Icon />
                    </Box>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      <Divider />

      {/* Downloads */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }} id="downloads">
        <Typography variant="h4" component="h2" textAlign="center" fontWeight={600} gutterBottom>
          Descargar el agente
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 5 }}>
          Elegí tu sistema operativo y empezá en minutos.
        </Typography>

        <Alert severity="info" sx={{ mb: 4 }}>
          <strong>¿Cómo funciona?</strong> Descargá el portable, extraé el ZIP, ejecutá el{' '}
          <code>remote-assist-portable.exe</code> — listo. Aparece un ícono en la bandeja del
          sistema. Para que arranque con Windows o corra antes del login, instalá como servicio
          desde el panel (un clic, con UAC).
        </Alert>

        <Grid container spacing={3}>
          {downloadOptions.map((option, idx) => {
            const Icon = option.icon;
            return (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={`${option.os}-${idx}`}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: option.recommended ? 2 : 1,
                    borderColor: option.recommended ? 'primary.main' : 'divider',
                  }}
                >
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                      <Icon color="primary" />
                      <Typography variant="h6" fontWeight={600}>
                        {option.title}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                      {option.recommended && (
                        <Chip label="Recomendado" color="primary" size="small" />
                      )}
                      <StageChip stage="beta" />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flex: 1 }}>
                      {option.description}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 2, fontStyle: 'italic' }}
                    >
                      {option.installNote}
                    </Typography>
                    <Button
                      variant={option.recommended ? 'contained' : 'outlined'}
                      startIcon={<DownloadIcon />}
                      href={option.url}
                      fullWidth
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Descargar
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {installers.length > 0 && (
          <Box sx={{ mt: 5 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Instaladores (servicio del sistema)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Alternativa al portable: instalan el agente como servicio. Generados en cada build de
              main.
            </Typography>
            <Grid container spacing={2}>
              {installers.map((a) => {
                const stage = stageFor(a.kind);
                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={a.name}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                          {INSTALLER_LABELS[a.kind] ?? a.name}
                          {a.arch ? ` · ${a.arch}` : ''}
                        </Typography>
                        {stage && (
                          <Box sx={{ mb: 1.5 }}>
                            <StageChip stage={stage} />
                          </Box>
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<DownloadIcon />}
                          href={a.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          fullWidth
                        >
                          Descargar
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
      </Container>

      <Divider />

      {/* System requirements */}
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Typography variant="h4" component="h2" textAlign="center" fontWeight={600} gutterBottom>
          Requisitos del sistema
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}
        >
          Windows es el build más maduro (stage <strong>BETA</strong>). macOS está en stage{' '}
          <strong>ALPHA</strong>. Linux permite enrolamiento y terminal, pero no escritorio remoto
          gráfico ni control de teclado/mouse — ver <code>docs/known-issues.md</code> para el
          roadmap.
        </Typography>
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Windows (amd64) — PC y laptops
            </Typography>
            <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2 }}>
              <li>Windows 10 / 11 / Server 2019+</li>
              <li>Arquitectura x86_64 (Intel/AMD)</li>
              <li>~50 MB disco</li>
              <li>ffmpeg auto-instalado al primer arranque</li>
              <li>Permisos estándar de usuario (no requiere admin para correr)</li>
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Windows (ARM64) — Surface Pro X, etc.
            </Typography>
            <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2 }}>
              <li>Windows 11 ARM64</li>
              <li>Snapdragon / Microsoft SQ1-SQ3</li>
              <li>~50 MB disco</li>
              <li>Mismas features que amd64</li>
              <li>
                Para convertir a servicio Windows: clic derecho en el ícono del tray → Install as
                Service (con UAC)
              </li>
            </Typography>
          </Grid>
        </Grid>
      </Container>

      {/* Footer CTA */}
      <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', py: 6, px: 2 }}>
        <Container maxWidth="md">
          <Stack spacing={3} alignItems="center" textAlign="center">
            <Typography variant="h4" component="h2" fontWeight={600}>
              ¿Listo para empezar?
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Instalá el agente en la máquina a asistir y empezá una sesión remota desde el panel.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                startIcon={<AdminIcon />}
                href={adminHref}
                sx={{ minWidth: 200 }}
              >
                {adminLabel}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Footer legal */}
      <Box sx={{ bgcolor: 'background.paper', py: 3, px: 2, borderTop: 1, borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
          >
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} Remote Assist.
            </Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
